import { z } from 'zod';

import { isoDateTimeSchema, nonNegativeIntSchema } from '../common/primitives';

/**
 * Eine Momentaufnahme, wie `restic` sie kennt — und wie der Master sie
 * weitergibt, damit das Portal später einen Rückkehrpunkt anbieten kann.
 */

/**
 * Die Kennung einer Momentaufnahme.
 *
 * `restic` zeigt acht Hexadezimalziffern an und akzeptiert sie auch so; die
 * vollständige Kennung hat 64. Beide sind erlaubt, weil der Betreiber die kurze
 * aus der Ausgabe kopiert und der Agent die lange meldet.
 *
 * Auch dieser Wert wird zu einem Argument von `restic` und ist deshalb eng
 * gefasst: Wäre er es nicht, brächte ein Rückweg-Aufruf aus dem Portal eine
 * beliebige Zeichenkette in die Argumentliste eines Programms, das auf einem
 * Server mit Docker-Socket läuft.
 */
export const snapshotIdSchema = z.string().regex(/^[a-f\d]{8,64}$/, {
  message: 'Muss aus 8 bis 64 Hexadezimalziffern in Kleinschreibung bestehen.',
});

export type SnapshotId = z.infer<typeof snapshotIdSchema>;

export const backupSnapshotSchema = z.object({
  snapshotId: snapshotIdSchema,
  createdAt: isoDateTimeSchema,
  /**
   * Größe der Momentaufnahme in Bytes, sofern `restic` sie kennt.
   *
   * Nullable, weil `restic snapshots --json` diese Angabe nur führt, wenn die
   * Momentaufnahme mit einer neueren Fassung erzeugt wurde. Eine 0 zu melden
   * wäre die falsche Auskunft: "unbekannt" und "leer" sind nicht dasselbe.
   */
  sizeBytes: nonNegativeIntSchema.nullable(),
});

export type BackupSnapshot = z.infer<typeof backupSnapshotSchema>;

/**
 * Das Ergebnis einer Sicherung oder eines Rückwegs.
 *
 * Warum das im Operationsergebnis steht und nicht im Heartbeat-Inventar, ist in
 * ADR 0012 begründet: Bei einem konvergierenden Verb ist das Inventar die
 * Wahrheit, und die Rückmeldung darf ausfallen. Eine Sicherung konvergiert
 * nicht — es gibt keinen Zustand am Node, an dem sich ablesen ließe, dass sie
 * gelaufen ist. Dort **ist** die Rückmeldung der Nachweis.
 */
export const backupResultSchema = z.object({
  /** Die erzeugte Momentaufnahme; beim Rückweg die verwendete. */
  snapshotId: snapshotIdSchema.nullable(),
  /**
   * Der Bestand nach `forget`/`prune` — also das, was ein Rückweg anbieten kann.
   * Höchstens 64 Einträge; die Aufbewahrung lässt deutlich weniger übrig.
   */
  snapshots: z.array(backupSnapshotSchema).max(64),
  /** Zuwachs dieses Laufes in Bytes, wie `restic backup --json` ihn meldet. */
  bytesAdded: nonNegativeIntSchema.nullable(),
});

export type BackupResult = z.infer<typeof backupResultSchema>;
